/**
 * Normalizes published / draft lesson shapes for the reader UI.
 */

export const getLessonHeroImage = (lesson) =>
  lesson?.coverImage || lesson?.imageUrl || '';

export const sortLessonsBySession = (lessons = []) =>
  [...(lessons || [])].sort((a, b) => {
    const na = Number(a?.sessionNumber) || 0;
    const nb = Number(b?.sessionNumber) || 0;
    if (na !== nb) return na - nb;
    return String(a?.title || '').localeCompare(String(b?.title || ''));
  });

/** Path segment for `/courses/:id/lessons/:segment` — matches LessonReaderPage (id, else raw array index). */
export const lessonReaderUrlSegment = (allLessons, lesson) => {
  const list = Array.isArray(allLessons) ? allLessons : [];
  if (!lesson) return '0';
  if (lesson.id != null && String(lesson.id).trim() !== '') {
    return String(lesson.id);
  }
  const i = list.findIndex(
    (x) =>
      Number(x?.sessionNumber) === Number(lesson?.sessionNumber) &&
      String(x?.title || '') === String(lesson?.title || ''),
  );
  if (i >= 0) return String(i);
  const j = list.indexOf(lesson);
  return j >= 0 ? String(j) : '0';
};

export const findLessonNeighborsForLesson = (lessons, currentLesson) => {
  const sorted = sortLessonsBySession(lessons);
  if (!currentLesson) {
    return { prev: null, next: null, index: -1, sorted };
  }
  const index = sorted.findIndex((l) => {
    if (currentLesson.id != null && l.id != null) {
      return String(l.id) === String(currentLesson.id);
    }
    return l === currentLesson;
  });
  if (index === -1) return { prev: null, next: null, index: -1, sorted };
  return {
    prev: index > 0 ? sorted[index - 1] : null,
    next: index < sorted.length - 1 ? sorted[index + 1] : null,
    index,
    sorted,
  };
};

const asList = (value) => {
  if (Array.isArray(value)) {
    return value.map((s) => String(s || '').trim()).filter(Boolean);
  }
  return [];
};

const nonEmpty = (v) => String(v || '').trim();

/**
 * Merge localized lesson content over English base (field-by-field fallback).
 */
const mergeLocalizedContent = (base, loc) => {
  const b = base && typeof base === 'object' ? base : {};
  if (!loc || typeof loc !== 'object') return { ...b };
  const pick = (k) => {
    const x = loc[k];
    if (x == null) return b[k];
    if (typeof x === 'string' && !String(x).trim()) return b[k];
    if (Array.isArray(x) && x.length === 0) return b[k];
    return x;
  };
  const keys = new Set([...Object.keys(b), ...Object.keys(loc)]);
  const out = {};
  keys.forEach((k) => {
    out[k] = pick(k);
  });
  return out;
};

/**
 * @param {object} lesson
 * @param {'en'|'si'|'ta'} lang
 * @returns {object} Lesson-shaped object for display (does not mutate source).
 */
export const localizeLesson = (lesson, lang) => {
  if (!lesson || lang === 'en') return lesson;

  const title =
    lang === 'si'
      ? nonEmpty(lesson.titleSinhala) || lesson.title
      : lang === 'ta'
        ? nonEmpty(lesson.titleTamil) || lesson.title
        : lesson.title;

  const description =
    lang === 'si'
      ? nonEmpty(lesson.descriptionSinhala) || lesson.description
      : lang === 'ta'
        ? nonEmpty(lesson.descriptionTamil) || lesson.description
        : lesson.description;

  const baseContent = lesson.content || {};
  const locContent =
    lang === 'si' ? lesson.contentSinhala : lang === 'ta' ? lesson.contentTamil : null;
  const content = mergeLocalizedContent(baseContent, locContent);

  const extraReadingsRaw =
    lang === 'si'
      ? lesson.extraReadingsSinhala
      : lang === 'ta'
        ? lesson.extraReadingsTamil
        : lesson.extraReadings;
  const extraReadings =
    Array.isArray(extraReadingsRaw) && extraReadingsRaw.length
      ? asList(extraReadingsRaw)
      : asList(lesson.extraReadings);

  const assignmentRaw =
    lang === 'si'
      ? lesson.assignmentSinhala
      : lang === 'ta'
        ? lesson.assignmentTamil
        : null;
  const assignment =
    lang !== 'en' &&
    assignmentRaw &&
    typeof assignmentRaw === 'object' &&
    (assignmentRaw.brief || assignmentRaw.title || assignmentRaw.instructions)
      ? { ...(lesson.assignment || {}), ...assignmentRaw }
      : lesson.assignment;

  return {
    ...lesson,
    title,
    description,
    content,
    extraReadings,
    assignment,
  };
};

/** Display title for a lesson in the session list (respects UI language). */
export const lessonDisplayTitle = (lesson, lang) => {
  if (!lesson) return '';
  if (lang === 'si' && nonEmpty(lesson.titleSinhala)) return lesson.titleSinhala;
  if (lang === 'ta' && nonEmpty(lesson.titleTamil)) return lesson.titleTamil;
  return lesson.title || '';
};

/** Normalizes AI `content.studentTutorial` steps for the reader. */
export const normalizeStudentTutorial = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((step, i) => {
      const title = String(step?.title || '').trim() || `Part ${i + 1}`;
      const subtitle = String(step?.subtitle || '').trim();
      const body = String(step?.body || '').trim();
      return { title, subtitle, body };
    })
    .filter((s) => s.body);
};

const hasTutorialReadingMaterial = (c) => {
  const intro = nonEmpty(c?.tutorialIntro);
  const summary = nonEmpty(c?.tutorialSummary);
  const steps = normalizeStudentTutorial(c?.studentTutorial);
  return Boolean(intro || summary || steps.length > 0);
};

/**
 * @param {'student' | 'admin'} [options.audience]
 * @param {'en'|'si'|'ta'} [options.lang] — for section titles that come from labels; body is already localized via localizeLesson.
 * @param {Record<string,string>} [options.labels] — from useLanguage().t pre-resolved reader.* strings
 */
export const buildReaderBlocks = (lesson, options = {}) => {
  const audience = options.audience === 'admin' ? 'admin' : 'student';
  const L = options.labels || {};
  const c = lesson?.content || {};
  const blocks = [];

  const tutorial = normalizeStudentTutorial(c.studentTutorial);
  const intro = nonEmpty(c.tutorialIntro);
  const summary = nonEmpty(c.tutorialSummary);
  const hasTutorial = hasTutorialReadingMaterial(c);
  const showFacilitatorOutline = audience === 'admin' || !hasTutorial;

  const pushTutorialRibbon = () => {
    blocks.push({
      type: 'tutorial_ribbon',
      id: 'tutorial-ribbon',
      title: L['reader.tutorialRibbon'] || 'Lesson tutorial',
    });
  };

  if (hasTutorial) {
    pushTutorialRibbon();

    if (intro) {
      blocks.push({
        type: 'tutorial_intro',
        id: 'tutorial-intro',
        title: L['reader.beforeYouBegin'] || 'Before you begin',
        body: intro,
      });
    }

    tutorial.forEach((step, i) => {
      blocks.push({
        type: 'tutorial_section',
        id: `tutorial-step-${i}`,
        sectionIndex: i + 1,
        title: step.title,
        subtitle: step.subtitle,
        body: step.body,
      });
    });

    if (summary) {
      blocks.push({
        type: 'tutorial_summary',
        id: 'tutorial-summary',
        title: L['reader.wrapUp'] || 'Wrap-up & next steps',
        body: summary,
      });
    }
  } else if (audience === 'student') {
    const overview = nonEmpty(c.overview);
    const description = nonEmpty(lesson?.description);
    if (description) {
      blocks.push({
        type: 'text',
        id: 'description',
        title: L['reader.aboutSession'] || 'About this session',
        body: description,
      });
    }
    if (overview) {
      blocks.push({
        type: 'text',
        id: 'overview',
        title: L['reader.whatYouCover'] || 'What you’ll cover',
        body: overview,
      });
    }
    const outcomes = asList(c.learningOutcomes?.length ? c.learningOutcomes : lesson?.learningObjectives);
    if (outcomes.length) {
      blocks.push({
        type: 'list',
        id: 'outcomes',
        title: L['reader.learningOutcomes'] || 'Learning outcomes',
        items: outcomes,
      });
    }
  }

  if (audience === 'admin' && hasTutorial) {
    blocks.push({
      type: 'text',
      id: 'facilitator-split',
      title: L['reader.facilitatorOutline'] || 'Facilitator outline & logistics',
      body:
        L['reader.facilitatorHint'] ||
        'Timing, setup, and delivery notes below. Learners primarily follow the learner tutorial above.',
    });
  }

  if (showFacilitatorOutline) {
    const description = nonEmpty(lesson?.description);
    if (description && audience === 'admin') {
      blocks.push({
        type: 'text',
        id: 'description',
        title: L['reader.aboutSession'] || 'About this session',
        body: description,
      });
    }

    const overview = nonEmpty(c.overview);
    if (overview && showFacilitatorOutline) {
      blocks.push({
        type: 'text',
        id: 'overview',
        title: L['reader.whatYouCover'] || 'What you’ll cover',
        body: overview,
      });
    }

    const outcomes = asList(c.learningOutcomes?.length ? c.learningOutcomes : lesson?.learningObjectives);
    if (outcomes.length && audience === 'admin') {
      blocks.push({
        type: 'list',
        id: 'outcomes',
        title: L['reader.learningOutcomes'] || 'Learning outcomes (reference)',
        items: outcomes,
      });
    }

    const flow = asList(c.lessonFlow);
    if (flow.length) {
      blocks.push({
        type: 'list',
        id: 'flow',
        title: L['reader.sessionFlow'] || 'How the session flows',
        items: flow,
      });
    }

    const materials = asList(c.materials);
    if (materials.length) {
      blocks.push({
        type: 'list',
        id: 'materials',
        title: L['reader.materials'] || 'What you’ll need',
        items: materials,
      });
    }

    const activities = asList(
      c.activities?.length ? c.activities : lesson?.practicalActivities,
    );
    if (activities.length) {
      blocks.push({
        type: 'list',
        id: 'activities',
        title: L['reader.activities'] || 'Activities & practice',
        items: activities,
      });
    }
  }

  const safety = asList(c.safetyAndHygiene);
  if (safety.length) {
    blocks.push({
      type: 'list',
      id: 'safety',
      title: L['reader.safety'] || 'Safety & hygiene',
      items: safety,
    });
  }

  const assessment = nonEmpty(c.assessment);
  if (assessment) {
    blocks.push({
      type: 'text',
      id: 'assessment',
      title: L['reader.assessment'] || 'How you’ll be assessed',
      body: assessment,
    });
  }

  const homePractice = nonEmpty(c.homePractice);
  if (homePractice) {
    blocks.push({
      type: 'text',
      id: 'home',
      title: L['reader.afterSession'] || 'After the session',
      body: homePractice,
    });
  }

  const instructorNotes = nonEmpty(c.instructorNotes);
  if (instructorNotes && audience === 'admin') {
    blocks.push({
      type: 'text',
      id: 'instructor',
      title: L['reader.tutorNotes'] || 'Tutor notes (staff only)',
      body: instructorNotes,
    });
  }

  const readings = asList(lesson?.extraReadings);
  if (readings.length) {
    blocks.push({
      type: 'list',
      id: 'readings',
      title: L['reader.furtherReading'] || 'Further reading',
      items: readings,
    });
  }

  const assignment = lesson?.assignment;
  if (assignment && (assignment.brief || assignment.title || assignment.instructions)) {
    blocks.push({
      type: 'assignment',
      id: 'assignment',
      title: assignment.title || 'Assignment',
      assignment,
    });
  }

  return blocks;
};

export const formatSessionMeta = (lesson) => {
  const parts = [];
  if (lesson?.sessionLabel) parts.push(lesson.sessionLabel);
  if (lesson?.sessionType) parts.push(String(lesson.sessionType).replace(/_/g, ' '));
  if (lesson?.estimatedMinutes) parts.push(`${lesson.estimatedMinutes} min`);
  return parts;
};
